import React, { memo } from 'react'
import { InputWithSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'
import { thicknessSliderTupleIfUnchanged } from '../helpers/slider-tuples'
import { SectionHeader } from './section-header'

const borderPresets = [
  { label: 'None', value: 0 },
  { label: '.5px', value: 0.5 },
  { label: '1px', value: 1 },
  { label: '4px', value: 4 },
  { label: '6px', value: 6 },
]

interface AppearanceSectionProps {
  colors: { fill: string; stroke: string }
  setColors: React.Dispatch<React.SetStateAction<{ fill: string; stroke: string }>>
  borderThickness: number[]
  setBorderThickness: React.Dispatch<React.SetStateAction<number[]>>
  disabled: boolean
}

/** Appearance: fill/stroke swatches and border thickness — local UI only, nothing is applied to shapes. */
function AppearanceSection({
  colors,
  setColors,
  borderThickness,
  setBorderThickness,
  disabled,
}: AppearanceSectionProps) {
  return (
    <>
      <SectionHeader>Appearance</SectionHeader>

      {/* Colors */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground leading-none">Colors</label>
        <div className="flex gap-2">
          <div
            className={cn('w-9 h-9 border border-input rounded-md', !disabled && 'cursor-pointer')}
            style={{ backgroundColor: colors.fill }}
            onClick={() => {
              if (disabled) return
              setColors((prev) => ({ ...prev, fill: '#e0e0e0' }))
            }}
          />
          <div
            className={cn(
              'w-9 h-9 border-4 border-input rounded-md bg-transparent',
              !disabled && 'cursor-pointer'
            )}
            style={{ borderColor: colors.stroke }}
            onClick={() => {
              if (disabled) return
              setColors((prev) => ({ ...prev, stroke: '#e5e5e5' }))
            }}
          />
        </div>
      </div>

      {/* Border */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground leading-none">Border</label>

        {/* Border Presets */}
        <ToggleGroup
          type="single"
          value={borderThickness[0].toString()}
          onValueChange={(val) => {
            if (!val) return
            const next = parseFloat(val)

            if (!Number.isFinite(next)) return
            setBorderThickness((prev) => thicknessSliderTupleIfUnchanged(prev, next))
          }}
          className="justify-start gap-1"
          disabled={disabled}
        >
          {borderPresets.map((preset) => (
            <ToggleGroupItem
              key={preset.value}
              value={preset.value.toString()}
              className="h-9 px-2 min-w-[32px] text-sm font-medium leading-normal"
            >
              {preset.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        {/* Border Slider */}
        <div className="flex items-center gap-[18px]">
          <Slider
            value={borderThickness}
            onValueChange={(v) =>
              setBorderThickness((prev) => thicknessSliderTupleIfUnchanged(prev, v[0] ?? 0))
            }
            max={10}
            step={0.1}
            className="flex-1 min-w-0 max-w-[130px]"
            disabled={disabled}
          />
          <InputWithSuffix
            type="text"
            value={borderThickness[0]}
            onChange={(e) => {
              const next = parseFloat(e.target.value) || 0

              setBorderThickness((prev) => thicknessSliderTupleIfUnchanged(prev, next))
            }}
            size="compact"
            placeholder="0"
            suffix={
              <span className="text-xs font-medium text-muted-foreground leading-none pr-0.5">
                px
              </span>
            }
            disabled={disabled}
          />
        </div>
      </div>
    </>
  )
}

/** Memoized: the object panel re-renders on every frame of a move, and this section doesn't show position. */
const MemoAppearanceSection = memo(AppearanceSection)

export { MemoAppearanceSection as AppearanceSection }
